/** Platform-specific behavior for login callbacks. */
export const AUTH_CALLBACK_MESSAGE_PREFIX = 'auth-callback:';
export const AUTH_CALLBACK_STORAGE_KEY = 'littlebird_auth_callback';

export interface LoginCallbackAdapter {
  /**
   * Open the window that will host the auth flow before the auth URL is
   * known. Must be called synchronously within the user gesture: popup
   * blockers (Safari and Firefox especially) reject window.open calls made
   * after an await, since they are no longer in the gesture's call stack.
   * Returns null where pre-opening is unsupported (e.g. Electron routes
   * window.open to the external browser).
   */
  preopenAuthWindow(): Window | null;
  /**
   * Navigate to the auth URL, reusing the pre-opened window when given.
   * Returns the window hosting the flow when available, so callers can
   * detect abandonment.
   */
  openAuthUrl(url: string, preopenedWindow?: Window | null): Window | null;
  listenForCallback(handler: (params: URLSearchParams) => void): () => void;
  getRedirectUri(): string;
  getDeviceId(): string | Promise<string>;
  generateStatePrefix?(): string;
}

/** Browser-based callback adapter for web and Electron popup windows. */
export class BrowserCallbackAdapter implements LoginCallbackAdapter {
  preopenAuthWindow(): Window | null {
    return window.open('', '_blank');
  }

  openAuthUrl(url: string, preopenedWindow?: Window | null): Window | null {
    if (preopenedWindow && !preopenedWindow.closed) {
      preopenedWindow.location.href = url;
      return preopenedWindow;
    }
    return window.open(url, '_blank');
  }

  listenForCallback(handler: (params: URLSearchParams) => void): () => void {
    const handleCallbackPayload = (payload: string) => {
      const parsedQuery = JSON.parse(payload);
      localStorage.removeItem(AUTH_CALLBACK_STORAGE_KEY);
      handler(new URLSearchParams(parsedQuery));
    };

    const listener = (event: MessageEvent) => {
      if (
        typeof event.data === 'string' &&
        event.data.startsWith(AUTH_CALLBACK_MESSAGE_PREFIX)
      ) {
        handleCallbackPayload(
          event.data.replace(AUTH_CALLBACK_MESSAGE_PREFIX, '')
        );
      }
    };

    const storageListener = (event: StorageEvent) => {
      if (event.key !== AUTH_CALLBACK_STORAGE_KEY || !event.newValue) return;
      handleCallbackPayload(event.newValue);
    };

    window.addEventListener('message', listener);
    window.addEventListener('storage', storageListener);
    return () => {
      window.removeEventListener('message', listener);
      window.removeEventListener('storage', storageListener);
    };
  }

  getRedirectUri() {
    return `${window.location.origin}/auth-callback`;
  }

  getDeviceId() {
    const browserName = navigator.userAgent;
    const randomString = Math.random().toString(36).substring(2, 15);
    return `${browserName}-${randomString}`;
  }
}
